import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { NAV_LINKS } from '../../content/site';
import { Logo, Button } from '../sv';
import { Menu, Globe, LayoutDashboard } from 'lucide-react';

function Navbar() {
  const { user, logout, isAuthenticated, isAdmin, isAgent } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(function () { setOpen(false); }, [location.pathname]);

  const portalHref = isAdmin ? '/admin' : isAgent ? '/agent' : '/dashboard';

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200" data-testid="site-header">
      <div className="sv-wrap h-[72px] flex items-center gap-8">
        <Link to="/" data-testid="navbar-logo" className="shrink-0"><Logo /></Link>

        <nav className="hidden lg:flex items-center gap-6 h-full flex-1" aria-label="Primary">
          {NAV_LINKS.map(function (l) {
            const active = location.pathname === l.href || (l.href !== '/services' && location.pathname.startsWith(l.href)) || (l.href === '/services' && location.pathname === '/services');
            return (
              <Link key={l.href} to={l.href} data-testid={"nav-" + l.label.toLowerCase()}
                className={"text-[15px] text-ink py-1.5 border-b-2 transition-colors duration-[120ms] " + (active ? 'border-blue-500' : 'border-transparent hover:border-blue-500')}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2 ml-auto shrink-0">
          <Button variant="ghost" size="sm" iconLeft={<Globe className="h-[18px] w-[18px]" strokeWidth={1.75} />} data-testid="nav-language">EN</Button>
          {isAuthenticated ? (
            <>
              <Button variant="secondary" size="sm" to={portalHref} iconLeft={<LayoutDashboard className="h-4 w-4" strokeWidth={1.75} />} data-testid="user-menu-trigger">{user?.first_name}</Button>
              <Button variant="ghost" size="sm" onClick={function () { logout(); navigate('/'); }} data-testid="logout-btn">Log out</Button>
            </>
          ) : (
            <Button variant="secondary" size="sm" to="/login" data-testid="login-btn">Log in</Button>
          )}
          <Button size="sm" to="/quote" data-testid="nav-quote">Get a quote</Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden ml-auto">
            <button className="p-2 rounded hover:bg-gray-100" data-testid="mobile-menu-btn" aria-label="Open menu"><Menu className="h-6 w-6 text-ink" strokeWidth={1.75} /></button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[86vw] max-w-sm p-0">
            <div className="p-5 border-b border-gray-200"><Logo /></div>
            <nav className="p-3 grid gap-0.5">
              {NAV_LINKS.map(function (l) { return <Link key={l.href} to={l.href} className="px-3 py-2.5 rounded text-[15px] text-ink hover:bg-gray-50">{l.label}</Link>; })}
              <Link to="/quote" className="px-3 py-2.5 rounded text-[15px] text-ink hover:bg-gray-50">Get a quote</Link>
              <Link to="/contact" className="px-3 py-2.5 rounded text-[15px] text-ink hover:bg-gray-50">Contact</Link>
            </nav>
            <div className="p-5 border-t border-gray-200 grid grid-cols-2 gap-2">
              {isAuthenticated ? (
                <>
                  <Button variant="primary" size="sm" to={portalHref}>My portal</Button>
                  <Button variant="secondary" size="sm" onClick={function () { logout(); navigate('/'); }}>Log out</Button>
                </>
              ) : (
                <>
                  <Button variant="secondary" size="sm" to="/login">Log in</Button>
                  <Button variant="primary" size="sm" to="/register">Register</Button>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}

export default Navbar;
